import { useState } from 'react'

import { friction, ru } from '../../lib/physics'
import { Arrow, ArrowDefs, Choice, Predict, Slider, useFrames } from './common'

/**
 * От покоя к скольжению (урок 39): брусок весом 1,9 Н тянут динамометром, сила растёт. Пока
 * брусок стоит, трение покоя равно тяге (наклонная прямая на графике); поехал — трение
 * скольжения μ·N и дальше не растёт (полка), лишняя сила разгоняет брусок.
 */

const ВЕС = 1.9 // Н
const ПОВЕРХНОСТИ = [
  { имя: 'лакированный стол', μ: 0.4 / 1.9 },
  { имя: 'картон', μ: 0.65 / 1.9 },
] as const
const МАКС = 1 // Н, предел ползунка

export default function FrictionLab() {
  const [тяга, setТяга] = useState(0.2)
  const [пов, setПов] = useState(0)
  const [x, setX] = useState(0)

  const { μ } = ПОВЕРХНОСТИ[пов]
  const f = friction(тяга, μ, ВЕС)

  useFrames((dt) => {
    if (!f.едет) return
    setX((p) => (p > 2 ? 0 : p + dt * (0.3 + f.a * 0.4)))
  })

  // график Fтр(Fтяги)
  const gx = (v: number) => 30 + (v / МАКС) * 140
  const gy = (v: number) => 120 - (v / МАКС) * 100
  const полка = μ * ВЕС
  const bx = 100 + (x % 2) * 40

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 120" role="img" aria-label="Брусок тянут динамометром">
        <ArrowDefs />
        <rect x={0} y={80} width={360} height={10} className={пов ? 'fill-book' : 'fill-ruler'} />
        <rect x={bx} y={44} width={70} height={36} rx={3} className="fill-body ink" strokeWidth={1.5} />
        <Arrow x1={bx + 70} y1={58} x2={bx + 70 + тяга * 90} y2={58} kind="el" label="F тяги" labelDy={-6} />
        <Arrow x1={bx} y1={72} x2={bx - f.Fтр * 90} y2={72} kind="heavy" label="F тр" labelDx={-34} labelDy={14} />
      </svg>
      <div className="lab-caption">
        {f.едет ? `Брусок скользит: трение скольжения ${ru(f.Fтр, 2)} Н, лишняя сила его разгоняет` : `Брусок стоит: трение покоя равно тяге, ${ru(f.Fтр, 2)} Н`}
      </div>

      <div className="lab-two">
        <svg className="lab-svg" style={{ maxWidth: 260 }} viewBox="0 0 190 140" role="img" aria-label="График силы трения от силы тяги">
          <line x1={30} y1={120} x2={180} y2={120} className="ink" />
          <line x1={30} y1={120} x2={30} y2={10} className="ink" />
          <polyline points={`${gx(0)},${gy(0)} ${gx(полка)},${gy(полка)} ${gx(МАКС)},${gy(полка)}`} className="ink-heavy" strokeWidth={2} fill="none" />
          <circle cx={gx(тяга)} cy={gy(f.Fтр)} r={5} className="fill-el" />
          <text x={180} y={134} textAnchor="end" className="lab-tick">F тяги, Н</text>
          <text x={34} y={14} className="lab-tick">F тр, Н</text>
        </svg>
        <div className="lab-readout lab-readout-col">
          <span className="el">F тяги = {ru(тяга, 2)} Н</span>
          <span className="heavy">F тр = {ru(f.Fтр, 2)} Н</span>
          <span>μ·N = {ru(μ, 2)} · {ru(ВЕС, 1)} Н = {ru(полка, 2)} Н</span>
        </div>
      </div>

      <Slider label="Тянем динамометром" value={тяга} min={0} max={МАКС} step={0.05} unit="Н" shown={ru(тяга, 2)} onChange={setТяга} />
      <div className="lab-sub">Поверхность</div>
      <Choice options={ПОВЕРХНОСТИ.map((p, i) => ({ value: i, label: p.имя }))} value={пов} onChange={setПов} />

      <Predict
        question="Брусок не сдвинулся при тяге 0,3 Н. Чему равна сила трения?"
        options={['0', '0,3 Н', '0,4 Н']}
        right={1}
        revealed={!f.едет && Math.abs(тяга - 0.3) < 0.01 && пов === 0}
        explain="Брусок стоит — силы уравновешены, поэтому трение покоя ровно 0,3 Н. Трение покоя подстраивается под тягу, пока не дойдёт до μ·N."
      />
    </div>
  )
}
